import React, { useState, useEffect } from 'react';
import styles from './ReportTableConfigModal.scss';
import { SettingOutlined } from '@ant-design/icons';
import { Tooltip, Button, Modal, Checkbox, Row, Col, Divider } from 'antd';
import WrappedTipsIcon from 'components/antd-extension/WrappedTipsIcon.jsx';
import DeleteIcon from 'svg/v2/newcs_dashboard_projectinformation_projectdelete_icon.svg';
import { ze } from 'utils/zhEn';
import { CS_STYLES } from 'utils/constants/style-constants';
import { useLocalStorageStateOfReportTableColumns } from 'utils/react-hooks/cs-local-storage-hooks';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ExceptionCategoryUtil } from 'utils/exception-category';
import { FieldName } from 'components/commons/IssueCrashFilter/IssueCrashFilterExUtil';
import {
  isMobile,
  isAndroid,
  isPc,
  isWechatMiniProgram,
  isSwitch,
  isPlaystation,
  PlatformUtil,
} from 'utils/platform';
import { EnvUtil } from 'utils/env-util';

export default function ReportTableConfigModal({ t, handleControlColumns, exceptionType, appId, pid }) {
  const [visible, setVisible] = useState(false);
  // 选中展示的字段
  const [selectFieldKeys, setSelectedKeys] = useState([]);
  // 将状态存储到localStorage
  const [reportSelectField, setReportSelectField] = useLocalStorageStateOfReportTableColumns();
  // 拖拽中的项目ID
  const [activeId, setActiveId] = useState(null);
  // 已选字段的显示顺序
  const [sortedFieldKeys, setSortedFieldKeys] = useState([]);

  const isJankCategory = ExceptionCategoryUtil.isJank(exceptionType);
  const isMobilePlatform = isMobile(pid);
  const isMobileOrMobileLike = PlatformUtil.isMobileOrMobileLike(pid);
  const isAndroidPlatform = isAndroid(pid);
  const isPcPlatform = isPc(pid, appId);
  const isWechatMiniProgramPlatform = isWechatMiniProgram(pid);
  const isSwitchPlatform = isSwitch(pid);
  const isPlaystationPlatform = isPlaystation(pid);

  // 默认展示的字段列表（在没有保存过配置的情况下使用）
  const defaultFieldList = [
    { value: FieldName.crashId, isHide: false },
    { value: FieldName.userId, isHide: false },
    { value: FieldName.version, isHide: false },
    { value: FieldName.crashTime, isHide: false },
    { value: FieldName.issueUploadTime, isHide: false },
    { value: FieldName.model, isHide: !isMobilePlatform },
    { value: FieldName.osVersion, isHide: false },
    { value: FieldName.jankAvgTime, isHide: !isJankCategory },
  ];

  const reportList = [
    { value: FieldName.crashId, label: t('REPORTRECORD.postId'), mustRequired: true },
    { value: FieldName.userId, label: t('REPORTDETAIL.userId') },
    { value: FieldName.deviceId, label: t('issueCrashFilterKey.deviceId') },
    { value: FieldName.version, label: t('REPORTRECORD.version') },
    { value: FieldName.crashTime, label: t('REPORTRECORD.发生时间') },
    { value: FieldName.issueUploadTime, label: t('REPORTRECORD.postTime') },
    { value: FieldName.model, label: t('REPORTRECORD.hardware'), isHide: !isMobilePlatform },
    { value: FieldName.osVersion, label: t('REPORTRECORD.osVersion') },
    { value: FieldName.processName, label: t('issueCrashFilterKey.processName'), isHide: !isMobileOrMobileLike },
    { value: FieldName.threadName, label: t('issueCrashFilterKey.threadName'), isHide: isWechatMiniProgramPlatform || isPlaystationPlatform },
    { value: FieldName.bundleId, label: t('issueCrashFilterKey.bundleId'), isHide: !isMobilePlatform },
    { value: FieldName.elapsedTime, label: t('issueCrashFilterKey.elapsedTime'), isHide: isWechatMiniProgramPlatform || isSwitchPlatform },
    { value: FieldName.appInBack, label: t('issueCrashFilterKey.appInBack'), isHide: !isMobileOrMobileLike },
    { value: FieldName.rom, label: t('issueCrashFilterKey.rom'), isHide: !isAndroidPlatform },
    { value: FieldName.appBit, label: t('issueCrashFilterKey.appBit'), isHide: !isAndroidPlatform },
    { value: FieldName.cpu, label: t('issueCrashFilterKey.cpu') },
    { value: FieldName.soc, label: t('issueCrashFilterKey.soc'), isHide: !isAndroidPlatform },
    { value: FieldName.gpu, label: t('issueCrashFilterKey.gpu'), isHide: !isPcPlatform },
    { value: FieldName.gpuDriverVersion, label: t('issueCrashFilterKey.gpuDriverVersion'), isHide: !isPcPlatform },
    { value: FieldName.expUid, label: t('issueCrashFilterKey.expUid') },
    { value: FieldName.countryOrRegion, label: t('issueCrashFilterKey.countryOrRegion'), isHide: EnvUtil.isChinaEnv() },
    { value: FieldName.vmType, label: t('issueCrashFilterKey.vmType'), isHide: !isAndroidPlatform },
    { value: FieldName.jankAvgTime, label: ze('卡顿Root时长(ms)', 'Jank Time (ms)'), isHide: !isJankCategory },
  ]
  const handleInitReportList = () => {
    // 设置选中状态，根据appId获取对应的值，如果没有则使用默认字段列表
    const appIdSpecificColumns = reportSelectField.appIdToReportTableColumns?.[appId];
    const defaultSelected = appIdSpecificColumns?.length
      ? appIdSpecificColumns
      : defaultFieldList.filter(x => !x.isHide).map(item => item.value);
    setSelectedKeys(defaultSelected);
    setSortedFieldKeys(defaultSelected);
    handleControlColumns(defaultSelected);
  }

  useEffect(() => {
    handleInitReportList()
  }, []);


  // 当选中字段变化时，更新排序后的字段列表
  useEffect(() => {
    // 如果有已排序的字段列表，先保留已排序字段的顺序，再添加新选中的字段
    if (sortedFieldKeys.length > 0) {
      // 第一步：保留已排序字段中仍然被选中的字段，保持它们的相对顺序
      const keptSortedFields = sortedFieldKeys.filter(key => selectFieldKeys.includes(key));

      // 第二步：找出新选中的字段（不在已排序列表中的字段）
      const newSelectedFields = selectFieldKeys.filter(key => !sortedFieldKeys.includes(key));

      // 第三步：按照reportList中的原始顺序对新选中的字段进行排序
      const orderedNewFields = reportList
        .filter(item => newSelectedFields.includes(item.value))
        .map(item => item.value);

      // 合并保留的已排序字段和按原始顺序排序的新字段
      const newSortedFields = [...keptSortedFields, ...orderedNewFields];
      setSortedFieldKeys(newSortedFields);
    } else {
      // 如果没有已排序的字段列表，按照reportList中的顺序设置
      const orderedFields = reportList
        .filter(item => selectFieldKeys.includes(item.value))
        .map(item => item.value);
      setSortedFieldKeys(orderedFields);
    }
  }, [selectFieldKeys]);

  // 处理拖拽开始
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  // 处理拖拽结束
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setSortedFieldKeys((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);

        // 所有字段都可以拖拽排序，包括mustRequired字段
        return arrayMove(items, oldIndex, newIndex);
      });

    }

    setActiveId(null);
  };

  // 处理删除字段
  const handleDeleteField = (fieldKey) => {
    // 检查字段是否为mustRequired
    const field = reportList.find(item => item.value === fieldKey);
    if (field && field.mustRequired) {
      return; // mustRequired字段不能删除
    }

    // 从sortedFieldKeys中过滤掉要删除的字段，保持剩余字段的相对顺序
    const newSortedFieldKeys = sortedFieldKeys.filter(key => key !== fieldKey);
    // 同时更新selectFieldKeys
    const newSelectedKeys = selectFieldKeys.filter(key => key !== fieldKey);

    setSelectedKeys(newSelectedKeys);
    setSortedFieldKeys(newSortedFieldKeys);
  };

  const onSubmit = () => {
    // 确保appIdToReportTableColumns对象存在
    const currentState = reportSelectField.appIdToReportTableColumns || {};
    // 更新当前appId对应的值，保留其他appId的值
    setReportSelectField({
      appIdToReportTableColumns: {
        ...currentState,
        [appId]: sortedFieldKeys
      }
    });
    // 调用父组件传递的回调函数，传递排序后的字段列表
    if (handleControlColumns) {
      handleControlColumns(sortedFieldKeys);
    }
    setVisible(false);
  }

  const onCancel = () => {
    setVisible(false);
  }

  return (
    <div>
      <Tooltip
        title={ze('字段设置', 'Field Settings')}
      >
        <Button
          onClick={() => {
            setVisible(true)
            handleInitReportList()
          }}
          onContextMenu={() => { }}
          icon={<SettingOutlined />}
        />
      </Tooltip>

      <Modal
        title={ze('字段设置', 'Field Settings')}
        width='860px'
        bodyStyle={{ minHeight: '200px' }}
        visible={visible}
        maskClosable={false}
        onCancel={() => onCancel()}
        okText={ze('保存', 'Save')}
        onOk={() => onSubmit()}
      >
        <div style={{ display: 'flex' }}>
          <div style={{ flexGrow: 1 }}>
            <div>
              <Checkbox.Group
                style={{ width: '100%' }}
                value={selectFieldKeys}
                onChange={v => setSelectedKeys(v)}
              >
                <Row> {reportList.map(item => {
                  if (item.isHide) return null
                  return <Col span={8} key={item.value} style={{ marginBottom: '16px' }}>
                    <Checkbox
                      value={item.value}
                      disabled={item.mustRequired}
                    >{item.label}</Checkbox>
                  </Col>
                }
                )} </Row>
              </Checkbox.Group>
            </div>
          </div>
          <Divider type='vertical' style={{ height: 'auto' }} />
          <div style={{ width: '200px', flexShrink: 0 }}>
            <div style={{ color: CS_STYLES.SUB_TEXT_COLOR, marginBottom: '12px' }}>{ze('当前选定的字段', 'Selected Fields')}</div>
            <DndContext
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={() => setActiveId(null)}
            >
              <SortableContext
                items={sortedFieldKeys}
                strategy={verticalListSortingStrategy}
              >
                {sortedFieldKeys.map((fieldKey) => {
                  const field = reportList.find(item => item.value === fieldKey);
                  if (!field) return null;

                  return (
                    <SortableItem
                      key={fieldKey}
                      id={fieldKey}
                      field={{ ...field, onDelete: handleDeleteField }}
                    >
                      {field.label}
                    </SortableItem>
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// 自定义拖拽句柄组件
const SortableItem = ({ id, children, field }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: false // 所有字段都可以拖拽
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    padding: '8px 12px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: isDragging ? '1px solid #1890ff' : '1px solid transparent',
    backgroundColor: isDragging ? '#e6f7ff' : 'transparent',
    marginBottom: '4px',
    userSelect: 'none'
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (field.onDelete) {
      field.onDelete(id);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={styles.sortableItem}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div
          style={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}
          {...attributes}
          {...listeners}
        >
          <span style={{ marginRight: '8px', fontSize: '14px' }}>⋮⋮</span>
        </div>
        <div style={{ flexGrow: 1 }}>{children}</div>
      </div>
      {!field.mustRequired && (
        <DeleteIcon
          style={{ color: CS_STYLES.SUB_TEXT_COLOR, cursor: 'pointer', marginLeft: '8px', width: '16px', height: '16px' }}
          onClick={handleDeleteClick}
        />
      )}
    </div>
  );
};
